<template>
  <div class="actions">
    <Tooltip v-if="undoRedoEnabled" placement="bottom">
      <template #trigger>
        <IconButton :disabled="undoHistory.length === 0" name="undo" @click="handleUndo" />
      </template>
      Cofnij akcję (maks. 5)
    </Tooltip>
    <Tooltip v-if="undoRedoEnabled" placement="bottom">
      <template #trigger>
        <IconButton :disabled="redoHistory.length === 0" name="redo" @click="handleRedo" />
      </template>
      Ponów akcję (maks. 5)
    </Tooltip>
    <div v-if="undoRedoEnabled" class="actions__separator" />

    <Tooltip v-if="canAssignDietToLeader" placement="bottom">
      <template #trigger>
        <IconButton name="school" @click="assignToLeaderModal = true" />
      </template>
      Przypisz jadłospis do Prowadzącego
    </Tooltip>
    <DietAssignToLeaderModal
      v-if="assignToLeaderModal && canAssignDietToLeader"
      :diet-type="dietType"
      :diets-ids="[dietId]"
      @close="assignToLeaderModal = false"
    />

    <Tooltip v-if="dietType !== 'university'" placement="bottom">
      <template #trigger>
        <IconButton name="person_add" @click="copyToPatientModal = true" />
      </template>
      Skopiuj do...
    </Tooltip>
    <DietCopyToPatientModal
      v-if="copyToPatientModal"
      :diet-type="dietType"
      :diet-id="dietId"
      :patient-id="patientId"
      title="Skopiuj do..."
      @close="copyToPatientModal = false"
    />

    <Tooltip v-if="dietType === 'patient'" placement="bottom">
      <template #trigger>
        <IconButton
          :disabled="!repeatedDishesAvailable"
          :type="repeatedDishesAndIngredientsActive ? 'warning' : 'default'"
          name="assignment_turned_in"
          @click="handleRepeatedDishes"
        />
      </template>
      <template v-if="repeatedDishesAvailable">
        Włącz/wyłącz podświetlanie potraw powtórzonych
      </template>
      <template v-else>
        <Text size="text-xs" color="color-base-white" weight="semibold">
          Pokaż/ukryj potrawy powtórzone
        </Text>
        <Text size="text-xs" color="color-gray-light-300" weight="regular">
          Podświetla potrawy, które powtarzają się w tym lub poprzednich jadłospisach tego pacjenta.
          <br><br>
          Przejdź do ustawień jadłospisu i dostosuj parametry funkcji do własnych potrzeb.
        </Text>
      </template>
    </Tooltip>

    <BaseLoader v-show="copyingDiet" class="base-loader--size-20 actions__loader" />
    <Tooltip v-if="canCopyDiet" placement="bottom">
      <template #trigger>
        <IconButton name="content_copy" @click="handleCopyDiet" />
      </template>
      Skopiuj jadłospis do schowka
    </Tooltip>
    <Tooltip v-else-if="canPasteDiet && isPasteDietDisabled" placement="bottom">
      <template #trigger>
        <IconButton :disabled="true" name="content_paste" @click="openPasteModal" />
      </template>
      <Text size="text-xs" color="color-base-white" weight="semibold">
        Nie można tu wkleić innego jadłospisu.
      </Text>
      <Text size="text-xs" color="color-gray-light-300" weight="medium">
        Ten jadłospis ma przypisane daty. Możesz kopiować elementy tylko w jego obrębie.
        <br><br>
        Aby wkleić inny jadłospis kliknij w wybrane daty, a następnie wybierz “Wyczyść daty”.
      </Text>
    </Tooltip>
    <Tooltip v-else-if="canPasteDiet && !isPasteDietDisabled" placement="bottom">
      <template #trigger>
        <IconButton name="content_paste" @click="openPasteModal" />
      </template>
      Wklej jadłospis ze schowka
    </Tooltip>
    <PasteDietModal
      v-if="pasteDietModal"
      :copied-calories="copiedDietCalories"
      :copied-days-count="copiedDaysCount"
      :copied-meals-count="copiedMealsCount"
      :current-calories="currentCalories"
      :current-days-count="dietDaysCount"
      :current-meals-count="currentMealsCount"
      @calories-click="track('diet_meal_plan_paste_modal_calories_clicked')"
      @clear="clearPasteStorage"
      @close="cancelPaste"
      @confirm="confirmPaste"
    />

    <Tooltip placement="bottom">
      <template #trigger>
        <IconButton name="print" @click="openPrintModal" />
      </template>
      Drukuj jadłospis
    </Tooltip>
    <PrintDietModal
      v-if="printDietModal"
      :diet-type="dietType"
      :diet-id="dietId"
      @close="printDietModal = false"
    />

    <Tooltip v-if="canShareDiet" placement="bottom">
      <template #trigger>
        <IconButton name="share" @click="openShareModal" />
      </template>
      Udostępnij jadłospis innemu użytkownikowi
    </Tooltip>
    <DietShareModal
      v-if="shareDietModal"
      :diet-type="dietType"
      :diets-ids="[dietId]"
      @close="shareDietModal = false"
    />

    <Tooltip v-if="canDeleteDiet" placement="bottom">
      <template #trigger>
        <IconButton
          name="delete"
          type="destructive"
          variant="outline"
          @click="deleteDietModal = true"
        />
      </template>
      Wyczyść jadłospis
    </Tooltip>
    <BaseModal v-if="deleteDietModal" @close="deleteDietModal = false">
      <template #title>Czy na pewno chcesz wyczyścić cały jadłospis?</template>
      Wyczyszczenie całego grafiku usunie aktualnie dodane potrawy, produkty i komentarze z całego
      jadłospisu.
      <template #actions>
        <StyledButton variant="outline" @click.once="deleteDietModal = false">Anuluj</StyledButton>
        <StyledButton @click.once="confirmDelete">Wyczyść</StyledButton>
      </template>
    </BaseModal>
  </div>
</template>

<script lang="ts" setup>
  import BaseLoader from '@/components/BaseLoader.vue'
  import BaseModal from '@/components/BaseModal.vue'
  import DietAssignToLeaderModal from '@/components/diets/DietAssignToLeaderModal.vue'
  import DietCopyToPatientModal from '@/components/diets/DietCopyToPatientModal.vue'
  import DietShareModal from '@/components/diets/DietShareModal.vue'
  import { IconButton } from '@/components/IconButton'
  import { StyledButton } from '@/components/StyledButton'
  import Tooltip from '@/components/ui/Navigation/Tooltip.vue'
  import Text from '@/components/ui/Text/Text.vue'
  import { PasteDietModal } from '@/pages/diets/components/PasteDietModal'
  import { PrintDietModal } from '@/pages/diets/components/PrintDietModal'

  import type { DietType } from '@/store/types'

  import { useDietDetailActions } from '../../composables/useDietDetailActions'

  const props = defineProps<{ dietId: number; dietType: DietType }>()

  const {
    assignToLeaderModal,
    canAssignDietToLeader,
    canCopyDiet,
    canDeleteDiet,
    canPasteDiet,
    canShareDiet,
    cancelPaste,
    clearPasteStorage,
    confirmDelete,
    confirmPaste,
    copiedDaysCount,
    copiedDietCalories,
    copiedMealsCount,
    copyingDiet,
    copyToPatientModal,
    currentCalories,
    currentMealsCount,
    deleteDietModal,
    dietDaysCount,
    handleCopyDiet,
    handleRedo,
    handleRepeatedDishes,
    handleUndo,
    isPasteDietDisabled,
    openPasteModal,
    openPrintModal,
    openShareModal,
    pasteDietModal,
    patientId,
    printDietModal,
    redoHistory,
    repeatedDishesAvailable,
    repeatedDishesAndIngredientsActive,
    shareDietModal,
    track,
    undoHistory,
    undoRedoEnabled,
  } = useDietDetailActions(props)
</script>

<style scoped>
  .actions {
    display: flex;
    flex-flow: row nowrap;
    gap: 2px;
    align-items: center;
  }

  .actions__separator {
    width: 2px;
    height: 18px;
    margin: 0 10px;
    background: var(--color-tertiary-text);
  }

  .actions__loader {
    margin: 4px;
  }
</style>
